import React, { createContext, useCallback, useContext, useState, useEffect } from 'react'

import { clearPaymentBlockedNotice } from '../payments/paymentBlockedNotice'

const AuthContext = createContext()
const AUTH_SESSION_CHANGED_EVENT = 'auth:session-changed'

function readStoredUser() {
  try {
    const rawUser = localStorage.getItem('user')
    return rawUser ? JSON.parse(rawUser) : null
  } catch {
    return null
  }
}

function getStoredUserExpirationMs(storedUser) {
  const exp = Number(storedUser?.data?.exp)
  return Number.isFinite(exp) && exp > 0 ? exp * 1000 : null
}

function isStoredUserExpired(storedUser) {
  const expirationMs = getStoredUserExpirationMs(storedUser)
  return expirationMs !== null && Date.now() >= expirationMs
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const storedUser = readStoredUser()
    return isStoredUserExpired(storedUser) ? null : storedUser
  })

  const clearSession = useCallback(() => {
    localStorage.removeItem('user')
    setUser(null)
    window.dispatchEvent(new Event(AUTH_SESSION_CHANGED_EVENT))
  }, [])

  const syncStoredUser = useCallback(() => {
    const storedUser = readStoredUser()
    if (isStoredUserExpired(storedUser)) {
      clearSession()
      return null
    }
    setUser(storedUser)
    return storedUser
  }, [clearSession])

  useEffect(() => {
    syncStoredUser()

    const handleStorageChange = (event) => {
      if (event.key && event.key !== 'user') {
        return
      }
      syncStoredUser()
    }

    const handleSessionChanged = () => {
      syncStoredUser()
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, handleSessionChanged)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, handleSessionChanged)
    }
  }, [syncStoredUser])

  useEffect(() => {
    if (!user) {
      return undefined
    }

    const expirationMs = getStoredUserExpirationMs(user)
    if (expirationMs === null) {
      return undefined
    }

    const remainingMs = expirationMs - Date.now()
    if (remainingMs <= 0) {
      clearSession()
      return undefined
    }

    const timeoutId = window.setTimeout(() => {
      clearSession()
    }, remainingMs)

    return () => window.clearTimeout(timeoutId)
  }, [clearSession, user])

  const getUser = () => {
    return user
  }

  const isAdmin = () => {
    const role = user?.data?.role || user?.data?.rol

    if (Array.isArray(role)) {
      return role.includes('ROLE_ADMIN') || role.includes('ADMIN')
    }

    return role === 'ROLE_ADMIN' || role === 'ADMIN'
  }
    
  const userIsAuthenticated = () => {
    return Boolean(user) && !isStoredUserExpired(user)
  }

  const userLogin = user => {
    localStorage.setItem('user', JSON.stringify(user))
    setUser(user)
    window.dispatchEvent(new Event(AUTH_SESSION_CHANGED_EVENT))
  }

  const userLogout = () => {
    clearPaymentBlockedNotice()
    clearSession()
  }

  const contextValue = {
    user,
    getUser,
    isAdmin,
    userIsAuthenticated,
    userLogin,
    userLogout,
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext

export function useAuth() {
  return useContext(AuthContext)
}

export { AuthProvider }
